import { useEffect } from 'react'
import { HOT_SLUG } from '../components/CategoryNav'

// 路徑 ↔ app state 對應：
//   "/"         → { mapOpen: false, slug: null }     (全部)
//   "/hot"      → { mapOpen: false, slug: HOT_SLUG } (🔥 吸睛度)
//   "/map"      → { mapOpen: true,  slug: null }     (地圖/歌單模式)
//   "/<slug>"   → { mapOpen: false, slug: "<slug>" }
//
// 設計決策（user-approved）：
//   - 路徑式 URL，最乾淨可分享
//   - replaceState：返回鍵離開網站而不是循環切過的視角
//   - 深入連結進站 → 跳過 intro（呼叫端用 initialSlug !== undefined 來判斷）
//   - mapOpen 與 slug 並列；mapOpen 為 true 時 URL 強制是 /map

const HOT_PATH = 'hot'
const MAP_PATH = 'map'

export type FilterSlug = string | null

export type UrlState = {
  mapOpen: boolean
  slug: FilterSlug
}

/** 從 window.location 解讀；categories 還沒載入時也能用（純解析 path）。 */
export function readUrlState(): UrlState {
  if (typeof window === 'undefined') return { mapOpen: false, slug: null }
  const p = window.location.pathname.replace(/^\/+|\/+$/g, '')
  if (p === '') return { mapOpen: false, slug: null }
  if (p === MAP_PATH) return { mapOpen: true, slug: null }
  if (p === HOT_PATH) return { mapOpen: false, slug: HOT_SLUG }
  return { mapOpen: false, slug: p.split('/')[0] || null }
}

/** 舊 API 相容 — 給只關心 slug 的呼叫端用 */
export function readSlugFromUrl(): FilterSlug {
  return readUrlState().slug
}

function stateToPath(s: UrlState): string {
  if (s.mapOpen) return '/' + MAP_PATH
  if (s.slug === null) return '/'
  if (s.slug === HOT_SLUG) return '/' + HOT_PATH
  return '/' + s.slug
}

/**
 * 把 app URL state 跟 window.location 雙向綁定。
 * - state 變動 → replaceState 同步 URL（不增加歷史條目）
 * - 瀏覽器上下頁（少數情況下會有歷史條目）→ onPopState 帶回新 state
 */
export function useCategoryUrl(
  state: UrlState,
  onPopState: (next: UrlState) => void
) {
  // state 變動 → 同步 URL
  useEffect(() => {
    const path = stateToPath(state)
    if (path === window.location.pathname) return
    window.history.replaceState(state, '', path + window.location.search + window.location.hash)
  }, [state.mapOpen, state.slug]) // eslint-disable-line react-hooks/exhaustive-deps

  // popstate
  useEffect(() => {
    const handler = () => onPopState(readUrlState())
    window.addEventListener('popstate', handler)
    return () => window.removeEventListener('popstate', handler)
  }, [onPopState])
}
